import Album from "lucide-react/dist/esm/icons/album";
import Clock3 from "lucide-react/dist/esm/icons/clock-3";
import Copy from "lucide-react/dist/esm/icons/copy";
import Disc3 from "lucide-react/dist/esm/icons/disc-3";
import FolderHeart from "lucide-react/dist/esm/icons/folder-heart";
import Heart from "lucide-react/dist/esm/icons/heart";
import Library from "lucide-react/dist/esm/icons/library";
import ListMusic from "lucide-react/dist/esm/icons/list-music";
import Plus from "lucide-react/dist/esm/icons/plus";
import Radio from "lucide-react/dist/esm/icons/radio";
import Tags from "lucide-react/dist/esm/icons/tags";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import TriangleAlert from "lucide-react/dist/esm/icons/triangle-alert";
import UserRound from "lucide-react/dist/esm/icons/user-round";
import type { LibraryView, Track } from "../model";
import { splitArtists } from "../services/artists";
import { useLibraryStore } from "../store/libraryStore";

interface SidebarProps { onNewPlaylist(): void; onDeletePlaylist(id: string): void; }

function unique(tracks: Track[], picker: (track: Track) => string[]) {
  return [...new Set(tracks.flatMap(picker).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

export function Sidebar({ onNewPlaylist, onDeletePlaylist }: SidebarProps) {
  const tracks = useLibraryStore((state) => state.tracks);
  const playlists = useLibraryStore((state) => state.playlists);
  const current = useLibraryStore((state) => state.view);
  const setView = useLibraryStore((state) => state.setView);
  const artists = unique(tracks, (track) => splitArtists(track.artist));
  const albums = unique(tracks, (track) => [track.album]);
  const genres = unique(tracks, (track) => track.genres);
  const labels = unique(tracks, (track) => [track.label]);
  const categories = unique(tracks, (track) => track.categories);
  const nav = (view: LibraryView, label: string, Icon: typeof Library, count?: number) =>
    <button className={current.view === view && !current.value ? "nav-item active" : "nav-item"} onClick={() => setView({ view })}>
      <Icon size={13} strokeWidth={1.7} /><span>{label}</span>{count !== undefined && <em>{count}</em>}
    </button>;
  const branch = (view: LibraryView, label: string, Icon: typeof Library, values: string[]) => <details className="nav-group">
    <summary><Icon size={13} strokeWidth={1.7} />{label}<em>{values.length}</em></summary>
    <div className="nav-children">{values.map((value) => <button key={value} title={value} className={current.view === view && current.value === value ? "nav-item active" : "nav-item"} onClick={() => setView({ view, value })}>{value}</button>)}</div>
  </details>;

  return <aside className="sidebar" aria-label="Library navigation">
    <div className="pane-title">LIBRARY INDEX</div>
    <nav>
      {nav("all", "All Tracks", Library, tracks.length)}
      {nav("favorites", "Favorites", Heart, tracks.filter((track) => track.favorite).length)}
      {nav("recent", "Recently Added", Clock3)}
      {nav("missing", "Missing Metadata", TriangleAlert)}
      {nav("duplicates", "Duplicates", Copy)}
      <div className="nav-separator" />
      {branch("artist", "Artists", UserRound, artists)}
      {branch("album", "Albums", Album, albums)}
      {branch("genre", "Genres", Radio, genres)}
      {branch("label", "Record Labels", Disc3, labels)}
      {branch("category", "Categories", Tags, categories)}
      <div className="nav-separator" />
      <div className="nav-heading"><span>PLAYLISTS</span><button aria-label="New playlist" title="New playlist" onClick={onNewPlaylist}><Plus size={12} /></button></div>
      {playlists.length === 0 && <p className="nav-empty">No playlists</p>}
      {playlists.map((playlist) => <div className="playlist-nav-row" key={playlist.id}>
        <button className={current.view === "playlist" && current.value === playlist.id ? "nav-item active" : "nav-item"} onClick={() => setView({ view: "playlist", value: playlist.id })}>
          {playlist.type === "smart" ? <FolderHeart size={13} /> : <ListMusic size={13} />}<span>{playlist.name}</span><em>{playlist.type === "manual" ? playlist.trackIds.length : "ƒ"}</em>
        </button>
        <button className="playlist-delete" aria-label={`Delete ${playlist.name}`} title={`Delete ${playlist.name}`} onClick={() => onDeletePlaylist(playlist.id)}><Trash2 size={12} /></button>
      </div>)}
    </nav>
  </aside>;
}
